
import React from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  CommandDialog, 
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem
} from "@/components/ui/command";
import navItems from "./sidebar/navItems";

// Function to flatten navigation items for search
const flattenNavItems = (items) => {
  let flattened = [];
  items.forEach(item => {
    flattened.push({
      title: item.title,
      path: item.path,
      icon: item.icon
    });
    
    if (item.children) {
      item.children.forEach(child => {
        flattened.push({
          title: child.title,
          path: child.path,
          parent: item.title
        });
      });
    }
  });
  return flattened;
};

export function CommandMenu() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const flattenedItems = React.useMemo(() => flattenNavItems(navItems), []);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        className="w-64 md:w-72 lg:w-96 justify-between text-muted-foreground text-sm"
        onClick={() => setOpen(true)}
      >
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4" />
          <span>Search...</span>
        </div>
        <kbd className="pointer-events-none hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-xs font-medium opacity-100">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search all resources and commands..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigation">
            {flattenedItems.map((item) => (
              <CommandItem
                key={`${item.parent || ''}${item.title}${item.path}`}
                onSelect={() => handleSelect(item.path)}
              >
                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                {item.title}
                {item.parent && <span className="ml-2 text-xs text-muted-foreground">({item.parent})</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
